import axios from 'axios';
import nifti from 'nifti-reader-js';

import vtkDataArray from 'vtk.js/Sources/Common/Core/DataArray';
import vtkImageData from 'vtk.js/Sources/Common/DataModel/ImageData';
import http from '@/http';

export default {
  namespaced: true,

  state: {
    // This must be an empty object (not null) for watchers to fire on the first set
    // TODO: or maybe it has to do with creation ordering?
    imageData: {},
    imageLoadProgress: null,
    imageError: null,
  },
  getters: {
    imageReady(state) {
      return Object.keys(state.imageData).length !== 0;
    },
    imageState(state, getters) {
      if (state.imageError !== null) {
        return 'error';
      }
      if (getters.imageReady) {
        return 'loaded';
      }
      if (state.imageLoadProgress !== null) {
        return 'loading';
      }
      return 'empty';
    },
  },
  mutations: {
    /**
     * @param {Object} state
     * @param {Object} payload
     * @param {vtkImageData} payload.imageData
     */
    setImage(state, { imageData }) {
      state.imageData = imageData;
      state.imageLoadProgress = null;
      state.imageError = null;
    },
    unsetImage(state) {
      state.imageData = {};
      state.imageLoadProgress = null;
      state.imageError = null;
    },
    setImageLoadProgress(state, { imageLoadProgress }) {
      state.imageLoadProgress = imageLoadProgress;
    },
    /**
     * @param {Object} state
     * @param {Object} payload
     * @param {string} payload.imageError
     */
    setImageError(state, { imageError }) {
      state.imageData = {};
      state.imageError = imageError;
    },
  },
  actions: {
    /**
     * @param {Object} context
     * @param {Object} payload
     * @param {TypedArray} payload.image
     * @param {nifti.NIFTI1} payload.header
     */
    loadNiftiImage({ commit }, { image, header }) {
      // eslint-disable-next-line no-console
      // console.log(header.toFormattedString());

      if (header.dims[0] !== 3) {
        commit('setImageError', {
          imageError: 'Non-3D image loaded.',
        });
        return;
      }

      const scalars = vtkDataArray.newInstance({
        values: image,
        name: 'ImageScalars',
      });

      const imageData = vtkImageData.newInstance({
        // NIfTI pixDims[1,2,3] contain X, Y, Z spacings
        spacing: header.pixDims.slice(1, 4),
        // pointData: scalars,
      });
      // NIfTI dims[1,2,3] contain X, Y, Z dimensions
      imageData.setDimensions(...header.dims.slice(1, 4));

      imageData.getPointData().setScalars(scalars);

      commit('setImage', { imageData });
    },

    /**
     * @param {Object} context
     * @param {Object} payload
     * @param {ArrayBuffer} payload.fileBuffer
     */
    loadNiftiBuffer({ commit, dispatch }, { fileBuffer }) {
      const rawFileBuffer = nifti.isCompressed(fileBuffer)
        ? nifti.decompress(fileBuffer)
        : fileBuffer;

      if (!nifti.isNIFTI(rawFileBuffer)) {
        commit('setImageError', {
          imageError: 'File cannot be read as NIfTI.',
        });
        return;
      }

      const header = nifti.readHeader(rawFileBuffer);

      const NIFTI_TYPE_ARRAYS = {
        // https://brainder.org/2012/09/23/the-nifti-file-format/
        2: Uint8Array,
        4: Int16Array,
        8: Int32Array,
        16: Float32Array,
        64: Float64Array,
        256: Int8Array,
        512: Uint16Array,
        768: Uint32Array,
      };
      const niftiType = header.datatypeCode;
      // eslint-disable-next-line no-prototype-builtins
      if (!NIFTI_TYPE_ARRAYS.hasOwnProperty(niftiType)) {
        commit('setImageError', {
          imageError: `File contains invalid NIfTI type: ${niftiType} (${header.getDatatypeCodeString(niftiType)})`,
        });
        return;
      }

      const imageBuffer = nifti.readImage(header, rawFileBuffer);
      const image = new NIFTI_TYPE_ARRAYS[niftiType](imageBuffer);

      dispatch('loadNiftiImage', { image, header });
    },

    async loadNiftiUrl({ commit, dispatch }, { fileUrl }) {
      // This will take some time
      commit('unsetImage');
      commit('setImageLoadProgress', { imageLoadProgress: 0 });

      let fileResponse;
      try {
        fileResponse = await axios.request({
          method: 'GET',
          url: fileUrl,
          responseType: 'arraybuffer',
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              const imageLoadProgress = (progressEvent.loaded / progressEvent.total) * 100;
              commit('setImageLoadProgress', { imageLoadProgress });
            }
          },
        });
      } catch (e) {
        // TODO
      }

      const fileBuffer = fileResponse.data;
      dispatch('loadNiftiBuffer', { fileBuffer });
    },
    async loadNiftiScan({ commit, dispatch }, { scanId }) {
      commit('unsetImage');

      const resp = await http.request({
        url: `/scan/${scanId}/`,
        method: 'get',
      });
      const scan = resp.data;

      if (!scan.nifti_file) {
        console.error(`No NIFTI file in scan ${scanId}`); // eslint-disable-line no-console
      }

      dispatch('loadNiftiUrl', { fileUrl: scan.nifti_file });
    },
  },
};
